import React, { useCallback, useState, useContext, useEffect } from 'react';
import {
  Avatar,
  Button,
  Heading,
  Tooltip as SuperopsTooltip,
  Modal,
  ButtonGroup,
  Page
} from '@superops/ui-design-system';
import nanoid from 'nanoid';
import { SVGIcon } from '@superops/super-icons';
import envConfig from '@nile/home/src/Config/env';
import {
  getTranslatedText,
  COLOR_MAP,
  TEXT_LENGTH,
  Editor,
  QueryBuilder,
  RunbookNodeActivatorContent,
  Loader,
  withErrorBoundary,
  COMMON_CONSTANTS,
  useIntl
} from '@nile/components';
import { injectIntl, FormattedMessage } from 'react-intl';
import TicketPropertiesCard from '@nile/banyan/src/Containers/CardContainers/TicketPropertiesCard';
import {
  GET_TICKET_AUTOMATION_RULE_META,
  GET_ENTITY_PROPERTIES,
  GET_TICKET_META_FOR_BULK_UPDATE,
  TicketClient
} from '@nile/graphql';
import { useQuery } from '@apollo/client';
import { cloneDeep, isEmpty, omit, isNil, omitBy } from 'lodash';

const BulkUpdatePropertyModal = (props) => {
  const {
    modalActive,
    setModalActive,
    onBulkUpdate,
    selectedRecords,
    bulkUpdateLoading
  } = props;
  const intl = useIntl();

  const formData = props?.autoActionformData || props?.data?.formData || {};

  const actionValueData =
    props?.autoActionformData?.actionValueData || formData?.actionValueData;
  const [configData, setConfigData] = useState([]);
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);

  const [ticketFieldsMeta, setTicketFieldsMeta] = useState({
    getTicketAutomationRuleMetaInfo: {
      actionGroups: {
        PROPERTY_UPDATE: []
      }
    }
  });

  const context = {
    updateAutomationRule: () => {},
    value: {
      action: [
        {
          type: 'PROPERTY_UPDATE',
          data: []
        }
      ]
    }
  };

  const { data: ticketUpdateData, loading: ticketUpdateDataLoading } = useQuery(
    GET_TICKET_META_FOR_BULK_UPDATE,
    {
      client: TicketClient,
      variables: {},
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
      // skip:
      //   ticketFieldsMeta.getTicketAutomationRuleMetaInfo.actionGroups
      //     .PROPERTY_UPDATE.length > 1,
      onCompleted(res) {
        // setSelectedItems(res);

        const ticketFieldsMetaTemp = {
          getTicketAutomationRuleMetaInfo: {
            actionGroups: {
              PROPERTY_UPDATE: res?.getTicketMetaForBulkUpdate?.propertyUpdate
            }
          }
        };
        setTicketFieldsMeta(ticketFieldsMetaTemp);
      },
      onError() {
        toast('error', 'error.page.config.default.title');
      }
    }
  );

  const {
    loading: entitiesLoading,
    error: entitiesError,
    data: allEntities
  } = useQuery(GET_ENTITY_PROPERTIES);

  const preparedQueries = {
    id: `g-${nanoid()}`,
    rules: [],
    combinator: 'AND',
    not: false
  };
  const [query, setQuery] = useState(preparedQueries);

  const updateConditions = (conditions) => {
    setConfigData(conditions);

    // Check if there are any valid values to enable save button
    const transformedData = transformData(
      conditions.map((arg) => omit(arg, ['id']))
    );
    const cleanedData = omitBy(
      transformedData,
      (v, k) => !k || isNil(v) || v === ''
    );
    setIsSaveEnabled(!isEmpty(cleanedData));
  };

  function transformData(configData) {
    const data = cloneDeep(configData);
    const result = {};
    const customFields = {};

    data.forEach((item) => {
      if (item?.name.startsWith('udf')) {
        customFields[item?.name] = item?.value;
      } else {
        result[item?.name] = item?.value;
      }
    });

    if (Object.keys(customFields).length > 0) {
      result.customFields = customFields;
    }

    return result;
  }

  const onSave = () => {
    const result = [...configData.map((arg) => omit(arg, ['id']))];

    const transformedData = transformData(result);
    const cleanedData = omitBy(
      transformedData,
      (v, k) => !k || isNil(v) || v === ''
    );

    //call api
    onBulkUpdate(cleanedData);
  };

  return (
    <>
      <Modal
        open={modalActive}
        onClose={() => setModalActive(!modalActive)}
        showCloseButton={false}
        large
        noScroll
      >
        <Page
          variant="primary"
          contentHeight="100px"
          title={<FormattedMessage id="app.btn.editfield" />}
          primaryAction={
            <ButtonGroup>
              <Button
                plainSecondary
                onClick={() => {
                  setModalActive(!modalActive);
                }}
              >
                <FormattedMessage id="app.close.btn" />
              </Button>
              <Button
                icon={<SVGIcon name="Save" />}
                primary
                onClick={onSave}
                disabled={!isSaveEnabled}
                loading={bulkUpdateLoading}
              >
                <FormattedMessage id="common.save" />
              </Button>
            </ButtonGroup>
          }
        >
          <div>
            {(entitiesLoading || ticketUpdateDataLoading) && <Loader />}
            {!entitiesLoading && !ticketUpdateDataLoading && (
              <TicketPropertiesCard
                context={context}
                allEntities={allEntities}
                ticketAutomationMeta={ticketFieldsMeta}
                intl={intl}
                handleUpdateParent={updateConditions}
                selectedRecords={selectedRecords}
                isRunBookAction
                filterCloseAndResolved
              />
            )}
          </div>
        </Page>
      </Modal>
    </>
  );
};
export default BulkUpdatePropertyModal;
